import { Box, Flex, Text } from '@workwhile/ui'
import { format } from 'date-fns'
import { formatCurrency } from 'lib/string'
import { useTipHistory } from 'queries/worker'
import { useWatch } from 'react-hook-form'
import { useTheme } from 'styled-components'

type Props = {
  workerId: number
  workId: number | null
}

/**
 * Given a worker ID and a work ID,
 * loads the tip history for that shift and
 * calculates subtotals and total with the new tip.
 */
export const ShiftTipHistory = ({ workerId, workId }: Props) => {
  const theme = useTheme()

  const { data, isLoading: isTipHistoryLoading } = useTipHistory(
    { workerId },
    {
      enabled: Boolean(workId && workerId),
    }
  )

  const amount = useWatch({ name: 'amount' })

  if (!workId || isTipHistoryLoading || !data) {
    return null
  }

  const paymentHistory =
    data?.workHistory?.find((tip) => tip.work.workId === workId)
      ?.paymentHistory || []

  const selectedTipHistory = data?.workHistory?.find(
    (tip) => tip.work.workId === workId
  )
  const totalPaymentAmount = Number(selectedTipHistory?.totalPaymentAmount) ?? 0
  const updatedAmount = typeof Number(amount) === 'number' ? Number(amount) : 0
  const updatedTotalAmount = totalPaymentAmount + updatedAmount
  const showTotals = updatedAmount && totalPaymentAmount

  return (
    <Flex flexDirection="column" gap={4}>
      <Flex flexDirection="column" gap={1}>
        <Text fontWeight="bold">Shift Tip History</Text>
        {paymentHistory.length ? (
          <>
            {paymentHistory.map((item) => (
              <Flex
                key={item.userPaymentId}
                justifyContent="space-between"
                width="100%"
              >
                <Text color={theme.colors.lightText} textAlign={'right'}>
                  {format(new Date(item.createdAt), 'MMM d, yyyy h:mm a')}
                </Text>
                <Text>{formatCurrency(item.amount)} </Text>
              </Flex>
            ))}
            <Text fontWeight="bold" textAlign={'right'}>
              {`Subtotal: ${formatCurrency(totalPaymentAmount)}`}
            </Text>
          </>
        ) : (
          <Text color={theme.colors.lightText}>
            {workId
              ? 'Worker has not received tips for this shift'
              : 'Select a shift to view tip history'}
          </Text>
        )}
      </Flex>

      {showTotals ? (
        <Box>
          <Text fontWeight="bold" textAlign={'right'}>
            {`Added Tip: ${formatCurrency(updatedAmount)}`}
          </Text>
          <Text fontWeight="bold" textAlign={'right'}>
            {`Total Tip: ${formatCurrency(updatedTotalAmount)}`}
          </Text>
        </Box>
      ) : null}
    </Flex>
  )
}
